import { SaveSettingValueRequest, SettingCache } from "services/KnowfullyAdminClient";
import { SettingTypeId, SettingsCodeConstants } from "types/common-types";
import { formatJson, validateJsonObject } from "./SettingJsonField";

export const hexColorRegExp = new RegExp(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
const emailRegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const typesThatRequireValue = [
  SettingTypeId.Bool,
  SettingTypeId.Int,
  SettingTypeId.Decimal,
  SettingTypeId.Date,
  SettingTypeId.DateTime,
  SettingTypeId.Time,
];
const domainRegExp =/^@[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const validateEmailList = (value: string): string | undefined => {
  if (!value?.trim()) {
    return undefined;
  }

  const items = value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item);
  const errors: string[] = [];

  items.forEach((item) => {
    if (item.startsWith("@")) {
      if (!domainRegExp.test(item)) {
        errors.push(`"${item}": Invalid domain format (should be @domain.com)`);
      }
    } else if (item.includes("@")) {
      if (!emailRegExp.test(item)) {
        errors.push(`"${item}": Invalid email format`);
      }
    } else {
      errors.push(`"${item}": Must be an email or domain (@domain.com)`);
    }
  });

  return errors.length > 0 ? errors.join(", ") : undefined;
};

/**
 * Validates the setting value based on the setting type and code constant.
 * Returns an error string if invalid, or undefined if valid.
 */
export const validateSettingValue = (
  setting: SettingCache,
  values: Pick<SaveSettingValueRequest, "value">,
  t: (params: { id: string }) => string
): string | undefined => {
  const requiresValue =
    typesThatRequireValue.includes(setting.settingTypeId as SettingTypeId) ||
    !!setting.settingOptions?.length;

  if (requiresValue && !values.value) {
    return t({ id: "requiredLabel" });
  }

  if (
    setting.settingTypeId === SettingTypeId.Color &&
    !hexColorRegExp.test(values.value)
  ) {
    return t({ id: "invalidColor" });
  }

  if (setting.settingTypeId === SettingTypeId.Json) {
    const jsonError = validateJsonObject(values.value, t);
    if (jsonError) {
      return jsonError;
    }
  }

  if (
    setting.codeConstant ===
    SettingsCodeConstants.RemoveEmailFromManualCreditSubmission
  ) {
    const emailListError = validateEmailList(values.value);
    if (emailListError) {
      return emailListError;
    }
  }

  return undefined;
};

/**
 * Applies any submit-time transformations to a setting value (e.g. JSON formatting).
 */
export const prepareSettingValue = (
  value: string,
  settingTypeId: string | undefined
): string => {
  if (settingTypeId === SettingTypeId.Json && value) {
    return formatJson(value);
  }
  return value;
};
